// Lista de códigos o de cupones sobre el panel común: búsqueda, estado y cadena, y la tabla con
// orden por columna. Buscar y ordenar corre en el cliente sobre lo cargado.
import type { ReactNode } from 'react';
import { BudgetBar } from '@/shared/patterns/BudgetBar';
import type { DataTableColumn } from '@/shared/patterns/DataTable';
import {
    describeValidityCountdown,
    LifecycleListPanel,
    LifecycleNameCell,
    LifecycleNotice,
} from '@/shared/patterns/Lifecycle';
import type { ChainDiscountCode } from '../api/useDiscountCodes';
import { DISCOUNT_CODE_HELP } from '../constants/helpText';
import { ALL_RETAILERS } from '../constants/retailerFilter';
import { ALL_CODE_STATUSES, CODE_VIEW_COPY, codesLoadedNotice } from '../constants/discountCodes';
import {
    codeStatusFilterOptions,
    isDimmedCodeStatus,
    toggleCodeSort,
    type CodeListSort,
    type CodeSortKey,
    type CodeView,
} from '../lib/discountCodesList';
import { formatCodeUses, formatCodeValidity, formatDiscount } from '../lib/discountCodesRow';
import { CodeStatusBadge } from './CodeStatusBadge';

/** Por debajo de este ancho la tabla scrollea dentro de su panel, en vez de cortar los encabezados. */
const TABLE_MIN_WIDTH = '520px';

export interface DiscountCodesListPanelProps {
    view: CodeView;
    items: ChainDiscountCode[];
    loaded: boolean;
    /** Cuántos se trajeron del back antes de buscar; con `hasMore` se avisa que hay más. */
    loadedCount: number;
    hasMore: boolean;
    /** Día CDMX `YYYY-MM-DD` contra el que se cuenta la vigencia. */
    today: string;
    search: string;
    onSearchChange: (search: string) => void;
    status: string;
    onStatusChange: (status: string) => void;
    sort: CodeListSort;
    onSortChange: (sort: CodeListSort) => void;
    selectedId: string | null;
    onSelect: (code: ChainDiscountCode) => void;
    /** `null` sin alta: los cupones no se crean a mano, o falta el permiso. */
    onCreate: (() => void) | null;
    error: { title: string; body?: string; onRetry: () => void } | null;
    /** «Cargar más», o el aviso de falla, bajo la tabla. */
    footer?: ReactNode;
    /** Las cadenas de la lista junta; con 2+ se pintan su columna y su filtro. */
    retailers?: {
        names: ReadonlyMap<string, string>;
        value: string;
        onChange: (retailerId: string) => void;
    };
}

export function DiscountCodesListPanel({
    view,
    items,
    loaded,
    loadedCount,
    hasMore,
    today,
    search,
    onSearchChange,
    status,
    onStatusChange,
    sort,
    onSortChange,
    selectedId,
    onSelect,
    onCreate,
    error,
    footer,
    retailers,
}: DiscountCodesListPanelProps) {
    const copy = CODE_VIEW_COPY[view];
    const byRetailer = retailers !== undefined && retailers.names.size > 1;
    const columns: DataTableColumn<ChainDiscountCode, CodeSortKey>[] = [
        {
            key: 'code',
            label: view === 'ADMIN' ? 'Código' : 'Cupón',
            sortKey: 'code',
            cellOverflow: 'wrap',
            cell: (code) => (
                <LifecycleNameCell
                    name={code.codeText}
                    detail={`${formatDiscount(code.discount)} de descuento`}
                    mono
                    dimmed={isDimmedCodeStatus(code.status)}
                />
            ),
        },
        {
            key: 'status',
            label: 'Estado',
            sortKey: 'status',
            width: '84px',
            cellOverflow: 'nowrap',
            cell: (code) => <CodeStatusBadge status={code.status} />,
        },
        {
            key: 'validity',
            label: 'Vigencia',
            sortKey: 'validity',
            width: '108px',
            cellTitle: (code) => formatCodeValidity(code),
            cell: (code) => (
                <span className="text-[10.5px] leading-[1.35]">
                    {describeValidityCountdown(code.validFrom, code.validUntil, today)}
                </span>
            ),
        },
        ...(byRetailer
            ? [
                  {
                      key: 'retailer',
                      label: 'Cadena',
                      width: '104px',
                      cell: (code: ChainDiscountCode) => (
                          <span className="text-[10.5px] leading-[1.35]">
                              {retailers.names.get(code.retailerId ?? '') ?? code.retailerId}
                          </span>
                      ),
                  },
              ]
            : []),
        {
            key: 'uses',
            label: 'Usos',
            sortKey: 'uses',
            width: '104px',
            cellOverflow: 'wrap',
            cell: (code) => <UsesCell code={code} />,
        },
    ];

    return (
        <LifecycleListPanel
            search={{
                label: view === 'ADMIN' ? 'Buscar código' : 'Buscar cupón',
                help: DISCOUNT_CODE_HELP.search,
                value: search,
                onChange: onSearchChange,
            }}
            status={{
                value: status,
                anyValue: ALL_CODE_STATUSES,
                options: codeStatusFilterOptions(view),
                onChange: onStatusChange,
            }}
            filters={
                byRetailer
                    ? [
                          {
                              label: 'Cadena',
                              help: copy.filterRetailer,
                              value: retailers.value,
                              options: [
                                  { value: ALL_RETAILERS, label: 'Todas las cadenas' },
                                  ...[...retailers.names.entries()]
                                      .sort(([, a], [, b]) => a.localeCompare(b, 'es-MX'))
                                      .map(([value, label]) => ({ value, label })),
                              ],
                              onChange: retailers.onChange,
                          },
                      ]
                    : []
            }
            count={{ value: items.length, one: copy.one, many: copy.many }}
            create={onCreate ? { label: 'Nuevo código', onClick: onCreate } : null}
            notice={
                hasMore && !error ? (
                    <LifecycleNotice>{codesLoadedNotice(loadedCount, copy)}</LifecycleNotice>
                ) : null
            }
            error={error}
            footer={footer}
            table={{
                columns,
                rows: items,
                rowKey: (code) => code.codeId,
                minWidth: TABLE_MIN_WIDTH,
                caption: copy.caption,
                sort,
                onSortToggle: (key) => {
                    onSortChange(toggleCodeSort(sort, key));
                },
                isRowSelected: (code) => code.codeId === selectedId,
                onRowClick: onSelect,
                isLoading: !loaded,
                skeletonRows: 6,
                empty: hasMore ? copy.emptyLoaded : copy.empty,
            }}
        />
    );
}

/** «3 de 10» con la barra debajo; sin tope, el hueco de la barra se reserva igual. */
function UsesCell({ code }: { code: ChainDiscountCode }) {
    const text = formatCodeUses(code);
    return (
        <div className="min-w-0">
            <div className="truncate text-[10px] leading-[1.3] tabular-nums" title={text}>
                {text}
            </div>
            {code.maxUses === null ? (
                <div aria-hidden className="mt-1.5 h-1.5" />
            ) : (
                <BudgetBar
                    label={`Usos de ${code.codeText}`}
                    consumed={code.uses}
                    max={code.maxUses}
                />
            )}
        </div>
    );
}
